import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useOptionalAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/contexts/LanguageContext";
import { trackEvent } from "@/lib/analytics";
import LanguageSelector from "@/components/LanguageSelector";
import { SafariOnlyBanner } from "@/components/SafariOnlyBanner";
import { PageSeo } from "@/components/PageSeo";
import { LandingTestimonials } from "@/components/landing/LandingTestimonials";
import { SchoolsMarkIcon } from "@/components/schools/SchoolsIcons";
import { useIsNativeMobile } from "@/hooks/useIsNativeIOS";


const mbLearnLogo = "/mblearn-logo-HD.png";

// Bilingual copy helper — landing strings live inline to avoid bloating translations.ts
const copy = {
  es: {
    eyebrow: "42,524 preguntas reales para el MBLEx",
    h1a: "Prepárate para el MBLEx.",
    h1b: "Tú puedes ser el siguiente.",
    sub: (
      <>
        Practica con <strong className="text-[#0F172A]">42,524 preguntas reales</strong>, en
        español e inglés, con IA que se adapta a lo que más necesitas estudiar.
      </>
    ),
    ctaStart: "🎯 Empieza 7 días gratis",
    ctaLogin: "Ya tengo cuenta — Iniciar sesión",
    ctaSchools: "¿Eres una escuela? Haz clic aquí",
    trust: ["✓ 7 días gratis", "✓ Acceso inmediato", "✓ Cancela cuando quieras"],
    statLabels: ["Preguntas reales", "Módulos MBLEx", "Niveles en total", "100% Bilingüe"],
    sectionLabel: "Contenido del examen",
    sectionTitle: "Practica módulo por módulo",
    modules: [
      "Anatomía y Fisiología",
      "Kinesiología",
      "Patología y Contraindicaciones",
      "Ética y Regulaciones",
      "Beneficios de la Manipulación",
      "Evaluación y Planificación",
    ],
    feats: [
      ["🧠", "Smart-15 Adaptativo", "Enfoca el estudio donde más lo necesitas."],
      ["💬", "Tutor IA", "Explicaciones detalladas por cada pregunta."],
      ["🏆", "Simulador MBLEx Real", "Practica bajo las condiciones del examen oficial."],
      ["🌎", "100% Bilingüe ES/EN", "Estudia en español, inglés, o alterna los dos."],
    ],
    trialBadge: "🎁 Prueba gratis por 7 días",
    pricingBadge: "Acceso PRO completo",
    pricingDesc: "42,524 preguntas · 7 módulos · Simulador · Tutor IA",
    pricingFeatures: [
      "7 módulos completos",
      "42,524 preguntas",
      "Simulador MBLEx",
      "Tutor IA",
      "Smart-15 adaptativo",
      "Bilingüe ES/EN",
      "Progreso detallado",
      "Cancela cuando quieras",
    ],
    pricingCta: "🎯 Empieza gratis 7 días",
    pricingNote: "Prueba gratis por 7 días · Acceso inmediato · Cancela cuando quieras",
    perMonth: "/mes",
    footerLinks: [
      ["Preguntas Frecuentes", "/faq"],
      ["Privacidad", "/legal/privacy"],
      ["Términos", "/legal/terms"],
      ["Reembolsos", "/legal/refunds"],
      ["Soporte", "/legal/support"],
    ],
    footerCopy: "© 2026 MBLearn. Todos los derechos reservados.",
    footerDisclaimer:
      "MBLearn es una plataforma independiente y no está afiliada ni respaldada por FSMTB.",
  },
  en: {
    eyebrow: "42,524 real MBLEx practice questions",
    h1a: "Prepare for the MBLEx.",
    h1b: "You can be next.",
    sub: (
      <>
        Practice with <strong className="text-[#0F172A]">42,524 real questions</strong>, in English
        and Spanish, powered by AI that adapts to what you need most.
      </>
    ),
    ctaStart: "🎯 Start 7 days free",
    ctaLogin: "I already have an account — Log in",
    ctaSchools: "Are you a school? Click here",
    trust: ["✓ 7 days free", "✓ Instant access", "✓ Cancel anytime"],
    statLabels: ["Real questions", "MBLEx modules", "Total levels", "100% Bilingual"],
    sectionLabel: "Exam content",
    sectionTitle: "Practice module by module",
    modules: [
      "Anatomy & Physiology",
      "Kinesiology",
      "Pathology & Contraindications",
      "Ethics & Regulations",
      "Benefits of Manipulation",
      "Assessment & Planning",
    ],
    feats: [
      ["🧠", "Smart-15 Adaptive", "Focus study where you need it most."],
      ["💬", "AI Tutor", "Detailed explanations on every question."],
      ["🏆", "Real MBLEx Simulator", "Practice under official exam conditions."],
      ["🌎", "100% Bilingual EN/ES", "Study in English, Spanish, or switch between both."],
    ],
    trialBadge: "🎁 7-day free trial",
    pricingBadge: "Full PRO access",
    pricingDesc: "42,524 questions · 7 modules · Simulator · AI Tutor",
    pricingFeatures: [
      "7 complete modules",
      "42,524 questions",
      "MBLEx simulator",
      "AI tutor",
      "Smart-15 adaptive",
      "Bilingual EN/ES",
      "Detailed progress",
      "Cancel anytime",
    ],
    pricingCta: "🎯 Start 7 days free",
    pricingNote: "7-day free trial · Instant access · Cancel anytime",
    perMonth: "/mo",
    footerLinks: [
      ["FAQ", "/faq"],
      ["Privacy", "/legal/privacy"],
      ["Terms", "/legal/terms"],
      ["Refunds", "/legal/refunds"],
      ["Support", "/legal/support"],
    ],
    footerCopy: "© 2026 MBLearn. All rights reserved.",
    footerDisclaimer:
      "MBLearn is an independent platform and is not affiliated with or endorsed by FSMTB.",
  },
} as const;

const moduleStyles = [
  { bg: "#EDE9FE", emoji: "🧠", fill: "#7C3AED", pct: 40 },
  { bg: "#DBEAFE", emoji: "📖", fill: "#3B82F6", pct: 57 },
  { bg: "#DCFCE7", emoji: "🩺", fill: "#22C55E", pct: 62 },
  { bg: "#FEF3C7", emoji: "⚖️", fill: "#F59E0B", pct: 70 },
  { bg: "#FCE7F3", emoji: "💆", fill: "#EC4899", pct: 48 },
  { bg: "#E0F2FE", emoji: "📋", fill: "#0EA5E9", pct: 55 },
];

const statNums = ["42,524", "7", "105", "ES/EN"];

const Index = () => {
  const { user } = useOptionalAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNativeMobile = useIsNativeMobile();
  const t = copy[language === "es" ? "es" : "en"];
  // On native Capacitor (iOS/Android) the "install PWA" prompt is meaningless —
  // the app is already installed. Route CTAs straight to register to stay
  // inside the WebView and avoid any perceived Safari hand-off.
  const registerHref = isNativeMobile ? "/auth/register" : "/auth/install-prompt";


  const fromLifecycleEmail =
    searchParams.get("from") === "reactivation" ||
    searchParams.get("utm_medium") === "lifecycle";

  // Authenticated user redirect (preserved from previous Index)
  useEffect(() => {
    if (!user || fromLifecycleEmail) return;

    const localFlag = localStorage.getItem("mblearn_onboarding_done");
    if (localFlag) {
      navigate("/dashboard", { replace: true });
      return;
    }

    const checkOnboardingInDB = async () => {
      try {
        const { supabase } = await import("@/integrations/supabase/client");
        const { count } = await supabase
          .from("quiz_attempts")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id)
          .limit(1);
        if (count && count > 0) {
          localStorage.setItem("mblearn_onboarding_done", "true");
          navigate("/dashboard", { replace: true });
          return;
        }
        const { count: progressCount } = await supabase
          .from("module_progress")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id)
          .limit(1);
        if (progressCount && progressCount > 0) {
          localStorage.setItem("mblearn_onboarding_done", "true");
          navigate("/dashboard", { replace: true });
          return;
        }
        navigate("/onboarding", { replace: true });
      } catch {
        navigate("/dashboard", { replace: true });
      }
    };
    checkOnboardingInDB();
  }, [user, fromLifecycleEmail, navigate]);

  if (user && !fromLifecycleEmail) {
    return (
      <div className="min-h-[100svh] bg-white flex items-center justify-center">
        <img
          src={mbLearnLogo}
          alt="MBLearn"
          className="w-[100px] h-auto animate-pulse"
        />
      </div>
    );
  }

  return (
    <div
      className="min-h-[100svh] text-[#0F172A]"
      style={{ background: "#F8FAFF", fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      <PageSeo
        path="/"
        title="MBLEx Practice Test & Study App — 42,524 Questions | MBLearn"
        description="Practice the MBLEx with 42,524 real questions in English and Spanish. 7 days free, cancel anytime. Bilingual MBLEx prep with AI tutor and real-exam simulator."
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "MBLearn",
            url: "https://www.mblearn.app",
            logo: "https://www.mblearn.app/mblearn-logo-HD.png",
          },
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "MBLearn",
            applicationCategory: "EducationalApplication",
            operatingSystem: "Web, iOS, Android",
            offers: {
              "@type": "Offer",
              price: "14.99",
              priceCurrency: "USD",
              priceSpecification: {
                "@type": "UnitPriceSpecification",
                price: "14.99",
                priceCurrency: "USD",
                unitText: "MONTH",
              },
            },
            description:
              "Bilingual MBLEx exam prep with 42,524 practice questions, 7 modules and full real-exam simulator.",
            url: "https://www.mblearn.app",
          },
        ]}
      />
      <SafariOnlyBanner />

      {/* NAV */}
      <nav
        className="bg-white border-b border-[#E2E8F0] sticky top-0 z-50 shadow-[0_1px_8px_rgba(0,0,0,0.06)]"
        style={{ padding: "12px 20px", paddingTop: "max(12px, env(safe-area-inset-top))" }}
      >
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <img src={mbLearnLogo} alt="MBLearn" className="h-[52px] w-auto" />
          <div className="flex items-center gap-3">
            <LanguageSelector silent />
          </div>
        </div>

      </nav>

      {/* HERO */}
      <section
        className="px-5 pt-5 pb-5 text-center"
        style={{
          background:
            "linear-gradient(160deg, #F5F3FF 0%, #EFF6FF 60%, #F0FDF4 100%)",
        }}
      >
        <h1 className="text-[30px] font-black leading-tight tracking-tight mb-3">
          {t.h1a}
          <br />
          <span className="text-[#16A34A]">{t.h1b}</span>
        </h1>
        <p className="text-[15px] text-[#64748B] max-w-md mx-auto mb-6 leading-relaxed">
          {t.sub}
        </p>
        <div className="flex flex-col items-center gap-2.5">
          <Link
            to={registerHref}
            onClick={() => trackEvent("landing_cta_clicked", "index", { button: "hero_cta" })}
            className="cta-glow inline-block w-full max-w-[320px] text-white text-[15px] font-bold py-3.5 px-7 rounded-xl"
          >
            {t.ctaStart}
          </Link>
          <Link
            to="/auth/login"
            onClick={() => trackEvent("landing_cta_clicked", "index", { button: "hero_login" })}
            className="text-sm text-[#64748B] underline font-medium"
          >
            {t.ctaLogin}
          </Link>
          <Link
            to="/schools"
            onClick={() => trackEvent("landing_cta_clicked", "index", { button: "hero_schools" })}
            className="group inline-flex items-center gap-1.5 text-sm text-[#64748B] hover:text-[#0F172A] transition-colors duration-200 mt-0.5"
            aria-label={language === "es" ? "Acceso institucional para escuelas" : "Institutional access for schools"}
          >
            <SchoolsMarkIcon className="h-3.5 w-3.5 text-[#94A3B8] group-hover:text-[#0F172A] transition-colors duration-200" />
            <span>{t.ctaSchools}</span>
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 ease-out group-hover:translate-x-1 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0" />
          </Link>
        </div>
      </section>

      {/* STATS */}
      <div className="bg-[#0F172A] py-4 px-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 max-w-3xl mx-auto">
          {statNums.map((num, i) => {
            const isLeftCol = i % 2 === 0;
            const isTopRow = i < 2;
            return (
              <div
                key={i}
                className={`text-center px-3 py-2 text-white ${
                  isLeftCol ? "border-r border-[#334155]" : ""
                } sm:border-r ${i === statNums.length - 1 ? "sm:border-r-0" : ""} ${
                  isTopRow ? "border-b sm:border-b-0 border-[#334155]" : ""
                }`}
              >
                <span className="block text-xl font-extrabold text-[#9b87f5]">{num}</span>
                <span className="text-[10px] text-[#94A3B8] uppercase tracking-wider">
                  {t.statLabels[i]}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* MODULES */}
      <section className="px-5 py-9 max-w-2xl mx-auto">
        <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#7C3AED] mb-1.5">
          {t.sectionLabel}
        </div>
        <h2 className="text-[22px] font-extrabold mb-5 tracking-tight">{t.sectionTitle}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {t.modules.map((name, i) => {
            const s = moduleStyles[i];
            return (
              <div
                key={i}
                className="bg-white rounded-xl p-3 border border-[#E2E8F0] flex items-center gap-2.5 shadow-[0_1px_4px_rgba(0,0,0,0.04)]"
              >
                <div
                  className="w-9 h-9 rounded-[9px] flex items-center justify-center text-base flex-shrink-0"
                  style={{ background: s.bg }}
                >
                  {s.emoji}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-semibold leading-tight mb-1.5">{name}</div>
                  <div className="h-1 bg-[#F1F5F9] rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${s.pct}%`, background: s.fill }}
                    />
                  </div>
                  <div className="text-[10px] font-bold text-[#64748B] mt-0.5">{s.pct}%</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* FEATURES */}
      <div className="bg-white border-y border-[#E2E8F0] py-6 px-5">
        <div className="max-w-2xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4">
          {t.feats.map(([icon, title, desc]) => (
            <div key={title} className="flex gap-2.5 items-start">
              <div className="text-xl flex-shrink-0 mt-0.5">{icon}</div>
              <div>
                <div className="text-[13px] font-bold mb-0.5">{title}</div>
                <div className="text-xs text-[#64748B] leading-snug">{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* PRICING */}
      <section className="px-5 py-9 max-w-md mx-auto text-center">
        <div
          className="bg-white rounded-2xl px-6 py-7 border-2 border-[#EDE9FE] shadow-[0_4px_24px_rgba(124,58,237,0.08)] relative overflow-hidden"
        >
          <div
            className="absolute top-0 inset-x-0 h-1"
            style={{ background: "linear-gradient(90deg, #7C3AED, #9b87f5)" }}
          />
          <div className="inline-block bg-[#DCFCE7] text-[#16A34A] text-xs font-bold px-3.5 py-1.5 rounded-full mb-3">
            {t.trialBadge}
          </div>
          <div className="block">
            <span className="inline-block bg-[#EDE9FE] text-[#7C3AED] text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-3.5">
              {t.pricingBadge}
            </span>
          </div>
          <div className="text-[44px] font-black tracking-tight leading-none mb-1">
            $14.99<span className="text-lg font-semibold text-[#64748B]">{t.perMonth}</span>
          </div>
          <div className="text-[13px] text-[#64748B] mb-5">{t.pricingDesc}</div>
          <ul className="text-left mb-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {t.pricingFeatures.map((f) => (
              <li
                key={f}
                className="flex items-center gap-1.5 text-xs text-[#334155] font-medium"
              >
                <span className="text-[#16A34A] font-extrabold">✓</span>
                {f}
              </li>
            ))}
          </ul>
          <Link
            to={registerHref}
            onClick={() =>
              trackEvent("landing_cta_clicked", "index", { button: "pricing_cta" })
            }
            className="cta-glow block w-full text-white text-[15px] font-bold py-3.5 px-7 rounded-xl mb-2.5"
          >
            {t.pricingCta}
          </Link>
          <div className="text-[11px] text-[#64748B]">{t.pricingNote}</div>
        </div>
      </section>

      {/* TESTIMONIALS (hidden until ≥4 approved reviews exist) */}
      <LandingTestimonials />

      {/* FOOTER */}
      <footer
        className="bg-[#0F172A] px-5 pt-6 text-center"
        style={{ paddingBottom: "max(24px, env(safe-area-inset-bottom))" }}
      >
        <img
          src={mbLearnLogo}
          alt="MBLearn"
          className="h-9 mx-auto mb-3 rounded-lg"
        />
        <div className="flex justify-center gap-4 flex-wrap mb-3">
          {t.footerLinks.map(([label, href]) => (
            <Link key={href} to={href} className="text-[#94A3B8] text-[11px] no-underline">
              {label}
            </Link>
          ))}
        </div>
        <div className="text-[10px] text-[#475569]">{t.footerCopy}</div>
        <div className="text-[9px] text-[#334155] mt-1.5 max-w-[400px] mx-auto">
          {t.footerDisclaimer}
        </div>
      </footer>
    </div>
  );
};

export default Index;
